import { cn } from "@/lib/utils";

// Below `sm`, dense tables become stacked cards: every meaningful field stays visible, the row action stays a real button.
export function StackedList({ items, fields, title, status, onOpen, canOpen, openLabel = "Open", testId, emptyText = "Nothing to show." }) {
  if (!items.length) return <p className="text-sm text-jigsaw-muted sm:hidden">{emptyText}</p>;
  return (
    <ul data-testid={testId} className="space-y-3 sm:hidden">
      {items.map((it) => (
        <li key={it.id} className="rounded-[10px] border border-jigsaw-border bg-white p-4">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-jigsaw-navy">{title(it)}</p>
            {status && status(it)}
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
            {fields.map((f) => (
              <div key={f.label} className={cn("min-w-0", f.full && "col-span-2")}>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">{f.label}</dt>
                <dd className={cn("truncate text-[14px] text-jigsaw-navy", f.mono && "font-mono-ref")}>{f.value(it) ?? "—"}</dd>
              </div>
            ))}
          </dl>
          {onOpen && (!canOpen || canOpen(it)) && (
            <button type="button" data-testid={`${testId}-open-${it.id}`} onClick={() => onOpen(it)}
              className="mt-3 inline-flex h-9 w-full items-center justify-center rounded-md border border-jigsaw-border text-[14px] font-medium text-jigsaw-navy hover:bg-jigsaw-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jigsaw-navy/30">
              {openLabel}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function Pagination({ page, pageSize, total, onPage, testId }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1, to = Math.min(total, page * pageSize);
  return (
    <nav data-testid={testId} aria-label="Pagination" className="flex flex-wrap items-center justify-between gap-2 text-[13px] text-jigsaw-muted">
      <span data-testid={`${testId}-summary`}>{total === 0 ? "No results" : `Showing ${from}–${to} of ${total}`}</span>
      <div className="flex items-center gap-1">
        <button type="button" data-testid={`${testId}-prev`} disabled={page <= 1} onClick={() => onPage(page - 1)} className="h-8 rounded-md border border-jigsaw-border px-3 text-jigsaw-navy disabled:opacity-40 hover:bg-jigsaw-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jigsaw-navy/30">Previous</button>
        <span className="px-2">Page {page} of {pages}</span>
        <button type="button" data-testid={`${testId}-next`} disabled={page >= pages} onClick={() => onPage(page + 1)} className="h-8 rounded-md border border-jigsaw-border px-3 text-jigsaw-navy disabled:opacity-40 hover:bg-jigsaw-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jigsaw-navy/30">Next</button>
      </div>
    </nav>
  );
}
